import $ from 'jquery';
import { badgeContainerSchema } from './HomepageBadgeSchema';
import type { Badge, BadgeContainer } from './HomepageBadgeSchema';
import './HomepageBadge.css';
import { type ZodSafeParseResult } from 'zod';

const COLLAPSE_DELAY_MS = 200;
const EXPAND_DELAY_MS = 500;

// CRO-14368: the transparent-header gradient drawn on the first component
// (see .stack_sort_area > section:has(.ss-badge-container) in
// _new-header.scss) needs the header's real rendered height, but has no CSS
// way to read it directly since it isn't that component's ancestor. Keeping
// this custom property in sync is what lets the component-side gradient line
// up with the header the badge is stacking against.
const TRANSPARENT_HEADER_HEIGHT_PROPERTY = '--ss-transparent-header-height';

const syncTransparentHeaderHeight = (header: HTMLElement): void => {
    document.documentElement.style.setProperty(TRANSPARENT_HEADER_HEIGHT_PROPERTY, `${header.getBoundingClientRect().height}px`);
};

const observeTransparentHeaderHeight = (): void => {
    const header = document.querySelector<HTMLElement>('.ss-site-header');
    if (!header) {
        return;
    }
    syncTransparentHeaderHeight(header);
    new ResizeObserver(() => syncTransparentHeaderHeight(header)).observe(header);
};

const init = (): void => {
    const scriptDataElem: HTMLScriptElement | null = document.querySelector(
        `script[type="application/json"][data-view-data-homepage-badge]`,
    );
    let zodParseResult: ZodSafeParseResult<BadgeContainer> | null = null;
    try {
        if (!scriptDataElem || !scriptDataElem.textContent) {
            throw new Error('HomepageBadge: no data found');
        }
        zodParseResult = badgeContainerSchema.safeParse(JSON.parse(scriptDataElem.textContent));
    } catch (error) {
        console.error('HomepageBadge: invalid data', error);
        return;
    }

    if (!zodParseResult.success) {
        console.error('HomepageBadge: invalid data', zodParseResult.error);
        return;
    }
    // remove the raw Json data from the DOM
    scriptDataElem?.remove();
    const data: BadgeContainer = zodParseResult.data;
    const visibleBadges = data.badges.filter((b: Badge): boolean => b.isVisible && b.url.trim() !== '');

    if (visibleBadges.length === 0) {
        return;
    }

    const container = buildBadgeContainer(visibleBadges);
    insertBadge(container, data.position);
    applyOffset(container, data.offset);
    bindInteractions(container);
    observeTransparentHeaderHeight();
};

const createBadgeLink = (badge: Badge): HTMLAnchorElement => {
    const link = document.createElement('a');
    link.className = 'ss-badge';
    link.setAttribute('aria-label', badge.altText);
    link.target = badge.badgeType === 'transparency' ? '_self' : '_blank';
    link.href = badge.url;

    if (badge.badgeType === 'data') {
        link.rel = 'noopener noreferrer';
    }

    const img = document.createElement('img');
    img.className = 'ss-badge-img';
    img.src = badge.imageUrl;
    img.alt = '';
    link.appendChild(img);

    return link;
};

const buildBadgeContainer = (visibleBadges: Badge[]): HTMLDivElement => {
    const countClass = visibleBadges.length === 1 ? 'one' : 'two';

    const container = document.createElement('div');
    container.className = `ss-badge-container ${countClass} open opened`;

    const closeBtn = document.createElement('button');
    closeBtn.className = 'ss-badge-close ss-badge-controller';
    closeBtn.setAttribute('aria-label', 'Collapse Badges');
    closeBtn.setAttribute('aria-expanded', 'true');
    const closeIcon = document.createElement('i');
    closeIcon.className = 'fa-solid fa-xmark';
    closeBtn.appendChild(closeIcon);
    container.appendChild(closeBtn);

    const inner = document.createElement('div');
    inner.className = 'ss-badge-container-inner';

    for (const badge of visibleBadges) {
        inner.appendChild(createBadgeLink(badge));
    }

    const openBtn = document.createElement('button');
    openBtn.className = 'ss-badge-open ss-badge-controller';
    openBtn.setAttribute('aria-label', 'Expand Badges');
    openBtn.setAttribute('aria-expanded', 'true');
    inner.appendChild(openBtn);

    container.appendChild(inner);

    return container;
};

const insertBadge = (badgeElement: HTMLDivElement, componentIndex: number): void => {
    $('.stack_sort_area section').each(function () {
        const display = $(this).find("[id*='stack_content']").css('display');
        if (typeof display !== 'undefined' && display === 'none') {
            $(this).addClass('ss-badge-invisible-component');
        }
    });

    const visibleComponents = $('.stack_sort_area section:not(.ss-badge-invisible-component)');
    const targetedComponent = visibleComponents.eq(componentIndex);
    targetedComponent.prepend(badgeElement);
};

const applyOffset = (container: HTMLDivElement, offset: number): void => {
    if (offset !== 0) {
        container.style.marginTop = `${offset}px`;
    }
};

interface BadgeControls {
    container: HTMLDivElement;
    badges: NodeListOf<Element>;
    controllers: NodeListOf<Element>;
    closeBtn: HTMLButtonElement;
    openBtn: HTMLButtonElement;
}

const collapseBadges = (ctx: BadgeControls): void => {
    ctx.container.classList.remove('opened');
    ctx.badges.forEach((el) => el.setAttribute('tabindex', '-1'));
    ctx.closeBtn.setAttribute('tabindex', '-1');
    setTimeout(() => {
        ctx.container.classList.remove('open');
        ctx.controllers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
        ctx.openBtn.focus();
    }, COLLAPSE_DELAY_MS);
};

const expandBadges = (ctx: BadgeControls): void => {
    ctx.container.classList.add('open');
    ctx.badges.forEach((el) => el.setAttribute('tabindex', '0'));
    ctx.closeBtn.setAttribute('tabindex', '0');
    setTimeout(() => {
        ctx.container.classList.add('opened');
        ctx.controllers.forEach((el) => el.setAttribute('aria-expanded', 'true'));
        ctx.closeBtn.focus();
    }, EXPAND_DELAY_MS);
};

const bindInteractions = (container: HTMLDivElement): void => {
    const closeBtn = container.querySelector<HTMLButtonElement>('.ss-badge-close');
    const openBtn = container.querySelector<HTMLButtonElement>('.ss-badge-open');

    if (!closeBtn || !openBtn) {
        console.error('HomepageBadge: expected control buttons not found in container');
        return;
    }

    const ctx: BadgeControls = {
        container,
        badges: container.querySelectorAll('.ss-badge'),
        controllers: container.querySelectorAll('.ss-badge-controller'),
        closeBtn,
        openBtn,
    };

    closeBtn.addEventListener('click', () => collapseBadges(ctx));
    openBtn.addEventListener('click', () => expandBadges(ctx));
};

$(() => {
    init();
});
